import { CLIENT_URL, SERVER_URL } from '@shared/config/api/api';
import {
  CreateProductReviewErrorResponse,
  CreateProductReviewRequest,
  CreateProductReviewResponse,
  ProductRatingType,
  ProductReviewsResponse,
  UploadImageResponse
} from '@shared/types/product';
import FetchData, { FetchDataResponse } from '@shared/utils/fetchData/fetchData';
import { refreshAccessToken } from '@widgets/Auth/api/refreshAccessToken/refreshAccessToken';

export const getProductRating = async (productId: string): Promise<ProductRatingType> => {
  const response = await fetch(`${SERVER_URL}/v2/review/${productId}/rating`, {
    next: { revalidate: 0 }
  });
  const data = await response.json();
  return await data;
};

export const getProductReviews = async (productId: string): Promise<ProductReviewsResponse> => {
  const response = await fetch(`${SERVER_URL}/v2/review/${productId}`, {
    next: { revalidate: 0 }
  });
  const data = await response.json();
  return await data;
};

const getCreateProductReviewErrorMessage = (
  data: CreateProductReviewResponse | CreateProductReviewErrorResponse | undefined
) => {
  if (!data || !('message' in data)) return '';

  return data.message;
};

export const createProductReview = async (
  productId: string,
  body: CreateProductReviewRequest
): Promise<FetchDataResponse<CreateProductReviewResponse>> => {
  const response = await FetchData.post<CreateProductReviewResponse | CreateProductReviewErrorResponse>(
    `v2/review/${productId}`,
    {
      body: JSON.stringify(body)
    }
  );

  if (response.code === 201) {
    return response as FetchDataResponse<CreateProductReviewResponse>;
  }

  return {
    ...response,
    data: undefined,
    message: response.message || getCreateProductReviewErrorMessage(response.data)
  };
};

export const uploadImage = async (productId: string, body: FormData): Promise<UploadImageResponse> => {
  const upload = () =>
    fetch(`${CLIENT_URL}/v2/review/${productId}/images`, {
      method: 'POST',
      body
    });

  let response = await upload();

  if (response.status === 401) {
    const { ok } = await refreshAccessToken();

    if (ok) {
      response = await upload();
    }
  }

  if (!response.ok) {
    throw new Error('Image upload failed');
  }

  return await response.json();
};
